import React from 'react';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import { faBullhorn, faCircle, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { uniq, without } from 'lodash';

import { blue, grape, green } from 'lib/css/colors';
import { DATE_FORMAT } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { ProductActiveStatus } from 'lib/enums/ProductActiveStatus';
import ProductUnlinkButton from 'components/global_products/detail_view/ProductUnlinkButton';
import RadioButton from 'components/shared/RadioButton';
import VerificationStatusIcon from 'components/global_products/detail_view/VerificationStatusIcon';

function DistributorRow({
  distributor,
  refetch,
  selectedIds,
  setSelectedIds,
  linkTypeChangeLogLoading,
  linkTypeChangeLog,
}) {
  const isChecked = selectedIds.vendorProducts.includes(distributor.id);
  const isArchived = distributor?.archived || false;
  const isDpPartner = distributor.global_vendor?.is_dp_partner || false;
  const isInfluenceEnabled = isDpPartner && distributor.global_vendor?.influenced_enabled;

  // Influence-enabled is a subset of DP partner; show the stronger signal only.
  const partnerMarker = isInfluenceEnabled ? (
    <OverlayTrigger overlay={<Tooltip>Influence-enabled DP partner</Tooltip>} placement="top">
      <FontAwesomeIcon icon={faBullhorn} color={grape} className="ml-2" />
    </OverlayTrigger>
  ) : isDpPartner ? (
    <OverlayTrigger overlay={<Tooltip>DP partner</Tooltip>} placement="top">
      <FontAwesomeIcon icon={faHandshake} color={blue} className="ml-2" />
    </OverlayTrigger>
  ) : null;

  // Only Active gets a marker; the slot keeps a fixed width so SKUs line up across rows.
  const activeStatusMarker = (
    <span className="d-inline-block mr-1" style={{ width: 10 }}>
      {distributor.active_status === ProductActiveStatus.Active && (
        <OverlayTrigger overlay={<Tooltip>{distributor.active_status}</Tooltip>} placement="top">
          <FontAwesomeIcon
            icon={faCircle}
            color={green}
            style={{ fontSize: 8, verticalAlign: 'middle' }}
            data-testid="active-status-icon"
          />
        </OverlayTrigger>
      )}
    </span>
  );

  const toggleCheck = () => {
    const isChecked = selectedIds.vendorProducts.includes(distributor.id);
    if (isChecked) {
      setSelectedIds({
        ...selectedIds,
        vendorProducts: without(selectedIds.vendorProducts, distributor.id),
        vendorProductObjects: without(selectedIds.vendorProductObjects, distributor),
      });
    } else {
      setSelectedIds({
        ...selectedIds,
        vendorProducts: uniq([...selectedIds.vendorProducts, distributor.id]),
        vendorProductObjects: uniq([...selectedIds.vendorProductObjects, distributor]),
      });
    }
  };

  return (
    <tr key={distributor.id} className={isArchived ? 'text-muted' : ''}>
      <td>
        <RadioButton
          box
          className="align-middle"
          small={true}
          checked={isChecked}
          onClick={() => toggleCheck()}
        />
      </td>
      <td>
        {isArchived ? (
          <OverlayTrigger
            overlay={<Tooltip>Product is archived.</Tooltip>}
            placement="top"
            delayShow={300}
            delayHide={150}
          >
            <span>{distributor.global_vendor.name}</span>
          </OverlayTrigger>
        ) : (
          distributor.global_vendor.name
        )}
        {partnerMarker}
      </td>
      <td className="text-nowrap">
        {activeStatusMarker}
        {distributor.vendor_sku}
      </td>
      <td>{distributor.cid}</td>
      <td>{distributor.name}</td>
      <td>{dayjs(distributor.date_created).format(DATE_FORMAT)}</td>
      <td className="text-center">
        {distributor.fsa_score ? distributor.fsa_score.toFixed(2) + ' %' : '-'}
      </td>
      <td className="text-center">{distributor.p0_score ? distributor.p0_score + ' %' : '-'}</td>
      <td>
        {linkTypeChangeLogLoading ? (
          <LoadingSpinner short={true} />
        ) : linkTypeChangeLog?.length > 0 ? (
          linkTypeChangeLog[0].meta_data.link_type
        ) : (
          '-'
        )}
      </td>
      <td className="text-center">
        <VerificationStatusIcon
          verificationStatus={distributor.verification_status}
          aiSuggestedVerification={distributor.ai_suggested_verification}
          isClaimed={distributor.is_claimed}
        />
      </td>
      <td>
        <ProductUnlinkButton
          globalProductId={distributor.global_product.id}
          vendorProductIds={[distributor.id]}
          verificationStatus={distributor.verification_status}
          refetch={refetch}
          disabled={!isChecked}
        />
      </td>
    </tr>
  );
}

DistributorRow.propTypes = {
  distributor: PropTypes.object,
  refetch: PropTypes.func,
  selectedIds: PropTypes.object,
  setSelectedIds: PropTypes.func,
  linkTypeChangeLogLoading: PropTypes.bool,
  linkTypeChangeLog: PropTypes.array,
};

export default DistributorRow;
